import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, ArrowRight } from 'lucide-react';
import { BLOG_POSTS } from '../constants';
import { ScrollReveal } from './ui/ScrollReveal';

export const BlogPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeBlogIndex, setActiveBlogIndex] = useState<number>(1); // Default to middle card

  const handlePostClick = (slug: string) => {
    navigate(`/blog/${slug}`);
  };

  const toSentenceCase = (text: string) => {
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  return (
    <div className="pt-32 min-h-screen bg-white px-2 lg:px-12">
      <div className="">
        {/* Header */}
        <ScrollReveal delay={0}>
          <div className="text-center mb-16 mx-auto">
             <h1 className="text-heading-large font-normal bg-gradient-to-r
         from-[#e20016] from-[0%]
         via-[#712f59] via-[40%]
         to-[#005d9c] to-[100%] bg-clip-text text-transparent mb-6 font-neue-haas">
                Expert Knowledge To Guide <br/>
                Smarter Material Choices Daily
            </h1>
            <p className="text-gray-500 max-w-xl mx-auto text-body">
                Discover expert insights, product guidance, and industry updates to help you make informed decisions for every project.
            </p>
        </div>
        </ScrollReveal>

        {/* Featured Section */}
        <ScrollReveal delay={0.05}>
          {BLOG_POSTS.length >= 3 && (
            <div className="flex gap-1 md:gap-4 h-[400px] md:h-[600px] overflow-hidden mb-32">
              {BLOG_POSTS.slice(0, 3).map((post, index) => {
                const isActive = activeBlogIndex === index;
                return (
                  <div
                    key={post.id}
                    onMouseEnter={() => setActiveBlogIndex(index)}
                    className={`relative rounded-lg overflow-hidden cursor-pointer transition-all duration-700 ${
                      isActive ? 'flex-[3]' : 'flex-1'
                    } ${isActive ? 'bg-black' : 'bg-white border-2 border-gray-200'}`}
                  >
                    {/* Active State: Full Background Image */}
                    <div
                      className={`absolute inset-0 bg-cover bg-center transition-opacity duration-700 ease-in-out z-0 ${
                        isActive ? 'opacity-100' : 'opacity-0'
                      }`}
                      style={{ backgroundImage: `url(${post.image})` }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                    </div>

                    {/* Badge: Animates position from Top Center (Inactive) to Top Left (Active) */}
                    <div
                      className={`absolute top-2 md:top-8 z-20 transition-all duration-700 ease-[cubic-bezier(0.25,0.1,0.25,1)] ${
                        isActive
                          ? 'left-2 md:left-8 translate-x-0 opacity-100'
                          : 'left-1/2 -translate-x-1/2 opacity-0 pointer-events-none'
                      }`}
                    >
                      <span className="bg-white px-3 md:px-5 py-1.5 md:py-2.5 text-[10px] md:text-xs font-bold uppercase tracking-widest text-black rounded-[4px] shadow-sm text-center md:text-left block leading-tight">
                        {toSentenceCase(post.tag)}
                      </span>
                    </div>

                    {/* Inactive Content: Centered Image Preview */}
                    <div
                      className={`absolute inset-0 flex flex-col items-center justify-center transition-all duration-700 ease-[cubic-bezier(0.25,0.1,0.25,1)] will-change-transform ${
                        isActive
                          ? 'opacity-0 scale-90 pointer-events-none'
                          : 'opacity-100 scale-100'
                      }`}
                    >
                      <div className="w-full h-full relative">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>

                    {/* Active Content: Bottom Left Text */}
                    <div
                      className={`absolute bottom-2 md:bottom-8 left-2 md:left-8 right-2 md:right-8 z-20 transition-opacity duration-300 ease-in-out ${
                        isActive
                          ? 'opacity-100 delay-500'
                          : 'opacity-0 delay-0 pointer-events-none'
                      }`}
                    >
                      <h3 className="text-sm md:text-3xl lg:text-4xl font-neue-haas text-white leading-tight mb-1 md:mb-4 text-left line-clamp-3 md:line-clamp-none">
                        {post.title}
                      </h3>
                      {post.description && (
                        <p className="text-white/90 text-xs md:text-sm leading-relaxed mb-3 md:mb-4 hidden md:block">
                          {post.description}
                        </p>
                      )}
                      <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-3">
                        <div className="flex items-center gap-2">
                          <img
                            src={post.avatar}
                            className="w-6 h-6 md:w-8 md:h-8 rounded-full border border-white/20"
                            alt={post.author}
                          />
                          <div>
                            <span className="block text-white font-bold text-xs md:text-sm">
                              {post.author}
                            </span>
                            <span className="block text-white/60 text-[10px] md:text-xs">
                              {post.role}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-white/90 text-[9px] md:text-sm font-medium">
                          <Clock size={12} className="md:w-4 md:h-4" /> {post.readTime}
                        </div>
                      </div>
                    </div>

                    {/* Animated Arrow Button */}
                    <button
                      type="button"
                      onClick={() => handlePostClick(post.slug)}
                      className={`absolute bottom-2 md:bottom-8 right-2 md:right-8 z-30 cursor-pointer bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-2 md:p-3 transition-opacity duration-300 ease-in-out group ${
                        isActive
                          ? 'opacity-100 delay-600'
                          : 'opacity-0 delay-0 pointer-events-none'
                      }`}
                      aria-label="View blog"
                    >
                      <ArrowRight
                        size={20}
                        className="md:w-6 md:h-6 text-white group-hover:translate-x-1 transition-transform"
                        strokeWidth={2.5}
                      />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollReveal>

        {/* Stories Grid */}
        <ScrollReveal delay={0.05}>
          <div className="mb-32">
            <h2 className="text-heading-large font-normal bg-gradient-to-r
         from-[#e20016] from-[0%]
         via-[#712f59] via-[40%]
         to-[#005d9c] to-[100%] bg-clip-text text-transparent mb-12 font-neue-haas">
                Explore More Stories
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-16">
                {BLOG_POSTS.map((post, index) => (
                    <ScrollReveal key={post.id} delay={0.05 + (index * 0.05)}>
                      <div className="group cursor-pointer" onClick={() => handlePostClick(post.slug)}>
                        <div className="h-[340px] rounded-[32px] overflow-hidden mb-8 relative">
                            <img src={post.image} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt={post.title} />
                            <div className="absolute top-8 left-8">
                                <span className="bg-[#EBEBEB] px-4 py-1.5 rounded-sm text-[10px] font-bold tracking-wider text-black">{toSentenceCase(post.tag)}</span>
                            </div>
                        </div>
                        
                        <div className="px-2">
                             <h3 className="text-heading-section font-medium text-gray-900 mb-6 group-hover:text-[#0055aa] transition-colors">
                                {post.title}
                             </h3>
                             
                             <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                     <img src={post.avatar} className="w-10 h-10 rounded-full object-cover" alt={post.author} />
                                     <div>
                                         <span className="block text-body font-bold text-gray-900">{post.author}</span>
                                         <span className="block text-body text-gray-500">{post.role}</span>
                                     </div>
                                </div>
                                <div className="flex items-center gap-2 text-gray-500 text-body font-medium">
                                    <Clock size={16} />
                                    <span>{post.readTime}</span>
                                </div>
                             </div>
                        </div>
                      </div>
                    </ScrollReveal>
                ))}
            </div>
          </div>
        </ScrollReveal>
      </div>
    </div>
  );
};